import { net } from './net.js';
import { storage } from './storage.js';
import { normalizeText, uid } from './utils.js';
import { cleanSegments, segmentsFromChapters } from './segments.js';

/* Biblioteca propia en un servidor Jellyfin o Emby (comparten casi toda la API).
   El cliente ofrece lo mismo que el de Xtream para que el catálogo lo trate como un proveedor
   más: comprobar la cuenta, traer el catálogo ya normalizado, la ficha de un título y la dirección
   del vídeo. Se entra con usuario y contraseña; el servidor devuelve un token que se guarda en el
   dispositivo y va en cada petición.

   Desde la web (https) solo se llega a servidores publicados en internet. A uno de la red local
   (http://192.168…) se llega desde la app de Android o de escritorio. */

const PAGE = 400;
// Emby solo devuelve lo que se le pide por nombre (el año, la nota, la duración…); Jellyfin da más por defecto.
const FIELDS = 'Genres,Overview,ProviderIds,DateCreated,MediaSources,OfficialRating,OriginalTitle,ProductionYear,CommunityRating,RunTimeTicks,PremiereDate,Container';
const TICKS = 10000000;

function deviceId() {
    let id = storage.get('deviceId', '');
    if (!id) {
        id = uid() + uid();
        storage.set('deviceId', id);
    }
    return id;
}

function fail(code, status) {
    return Object.assign(new Error(code), { code, status });
}

const catId = (name) => 'g-' + normalizeText(name).replace(/\s+/g, '-');

export class MediaServerClient {
    constructor(profile, { fetchImpl = null, fetchJson = null, authFallback = null } = {}) {
        this.profile = profile;
        this.kind = profile.type === 'emby' ? 'emby' : 'jellyfin';
        this.host = String(profile.host || '').replace(/\/+$/, '');
        this.fetch = fetchImpl || ((url, opts) => fetch(url, opts));
        this.fetchJson = fetchJson || ((url, opts) => net.fetchJson(url, opts));
        // Inicio de sesión a través del servidor de Filoteca, cuando el navegador no puede hacerlo.
        this.authFallback = authFallback;
        this.session = storage.get('ms-session:' + profile.id, null);
        this._playing = new Set(); // títulos con sesión de reproducción abierta en el servidor
    }

    get token() {
        return this.session ? this.session.token : '';
    }

    _authHeader() {
        return `MediaBrowser Client="Filoteca", Device="${typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent) ? 'Android' : 'Web'}", DeviceId="${deviceId()}", Version="1.0.0"`;
    }

    async login() {
        const body = JSON.stringify({ Username: this.profile.username, Pw: this.profile.password || '' });
        let data;
        // Jellyfin quiere la identificación en «Authorization»; Emby, en «X-Emby-Authorization».
        const names = this.kind === 'emby' ? ['X-Emby-Authorization', 'Authorization'] : ['Authorization', 'X-Emby-Authorization'];
        const attempt = async (name) => {
            const headers = { 'Content-Type': 'application/json', Accept: 'application/json' };
            headers[name] = this._authHeader();
            const r = await this.fetch(this.host + '/Users/AuthenticateByName', { method: 'POST', headers, body });
            if (r.status === 401 || r.status === 403) throw fail('auth', r.status);
            if (!r.ok) throw fail('http', r.status);
            return r.json();
        };
        try {
            try {
                data = await attempt(names[0]);
            } catch (e) {
                if (e.code !== 'http') throw e;
                data = await attempt(names[1]);
            }
        } catch (e) {
            if (e.code === 'auth' || !this.authFallback) throw e;
            // Sin respuesta directa (contenido mixto, CORS): lo intenta el servidor de Filoteca.
            data = await this.authFallback({ type: this.kind, host: this.host, username: this.profile.username, password: this.profile.password || '', header: this._authHeader() });
        }
        if (!data || !data.AccessToken || !data.User) throw fail('auth', 401);
        this.session = { token: data.AccessToken, userId: data.User.Id, user: data.User.Name, admin: !!(data.User.Policy && data.User.Policy.IsAdministrator), serverId: data.ServerId || '' };
        storage.set('ms-session:' + this.profile.id, this.session);
        return this.session;
    }

    _url(path, params) {
        const u = new URL(this.host + path);
        Object.keys(params || {}).forEach((k) => { if (params[k] != null && params[k] !== '') u.searchParams.set(k, params[k]); });
        // El token va en la URL (el <video> no puede enviar cabeceras). Jellyfin lo llama ApiKey;
        // Emby y los Jellyfin antiguos, api_key.
        if (this.token) {
            if (this.kind !== 'emby') u.searchParams.set('ApiKey', this.token);
            u.searchParams.set('api_key', this.token);
        }
        return u.toString();
    }

    /** GET con sesión; si el token ya no vale se entra de nuevo una vez. */
    async get(path, params, opts) {
        if (!this.session) await this.login();
        try {
            return await this.fetchJson(this._url(path, params), opts);
        } catch (e) {
            if (e.status !== 401) throw e;
            await this.login();
            return this.fetchJson(this._url(path, params), opts);
        }
    }

    /** Misma forma que XtreamClient.auth(): { user_info, server_info }. Una biblioteca propia no caduca. */
    async auth() {
        await this.login();
        let info = {};
        try {
            info = await this.fetchJson(this._url('/System/Info/Public'), { timeout: 10000 });
        } catch (e) { /* dato opcional */ }
        return {
            user_info: { username: this.session.user, auth: 1, status: 'Active', exp_date: null, max_connections: 0, active_cons: 0 },
            server_info: { url: this.host.replace(/^https?:\/\//, ''), name: info.ServerName || '', version: info.Version || '', product: info.ProductName || (this.kind === 'emby' ? 'Emby' : 'Jellyfin') }
        };
    }

    image(id, type, tag, width) {
        if (!tag) return '';
        const u = new URL(this.host + '/Items/' + id + '/Images/' + type);
        u.searchParams.set('maxWidth', String(width));
        u.searchParams.set('quality', '90');
        u.searchParams.set('tag', tag);
        return u.toString();
    }

    async _all(types) {
        const out = [];
        for (let start = 0; ; start += PAGE) {
            const r = await this.get('/Users/' + this.session.userId + '/Items', {
                IncludeItemTypes: types, Recursive: 'true', Fields: FIELDS, StartIndex: start, Limit: PAGE, EnableUserData: 'true',
                SortBy: 'DateCreated', SortOrder: 'Descending', EnableImageTypes: 'Primary,Backdrop', ImageTypeLimit: 1
            }, { timeout: 60000 });
            const items = (r && r.Items) || [];
            out.push.apply(out, items);
            if (items.length < PAGE || out.length >= (r.TotalRecordCount || 0)) break;
        }
        return out;
    }

    /** Lo visto según el servidor: { position (s), played, at (ms) }, o null si no trae datos de usuario. */
    _serverState(it) {
        const u = it && it.UserData;
        if (!u) return null;
        return { position: Math.round((Number(u.PlaybackPositionTicks) || 0) / TICKS), played: !!u.Played, at: u.LastPlayedDate ? Date.parse(u.LastPlayedDate) || 0 : 0 };
    }

    _base(it) {
        const genres = it.Genres || [];
        const tags = it.ImageTags || {};
        const backdrop = (it.BackdropImageTags || [])[0];
        const src = (it.MediaSources || [])[0] || {};
        return {
            id: String(it.Id),
            name: it.Name || '',
            originalName: it.OriginalTitle || '',
            poster: this.image(it.Id, 'Primary', tags.Primary, 500),
            backdrop: backdrop ? this.image(it.Id, 'Backdrop', backdrop, 1280) : '',
            categoryId: genres.length ? catId(genres[0]) : 'g-otros',
            categoryName: genres[0] || '',
            genre: genres.join(', '),
            plot: it.Overview || '',
            year: it.ProductionYear ? String(it.ProductionYear) : '',
            rating: it.CommunityRating ? Math.round(it.CommunityRating * 10) / 10 : 0,
            added: it.DateCreated ? Math.floor(Date.parse(it.DateCreated) / 1000) || 0 : 0,
            age: it.OfficialRating || '',
            tmdb: (it.ProviderIds && (it.ProviderIds.Tmdb || it.ProviderIds.tmdb)) || '',
            imdb: (it.ProviderIds && (it.ProviderIds.Imdb || it.ProviderIds.imdb)) || '',
            durationSecs: it.RunTimeTicks ? Math.round(it.RunTimeTicks / TICKS) : 0,
            ext: String(src.Container || it.Container || 'mp4').split(',')[0],
            server: this._serverState(it)
        };
    }

    /** Catálogo con la misma forma que el de un proveedor IPTV. Las categorías son los géneros. */
    async fetchCatalog() {
        const account = await this.auth();
        const [movies, shows] = await Promise.all([this._all('Movie'), this._all('Series')]);
        const vod = movies.map((it) => this._base(it));
        const series = shows.map((it) => Object.assign(this._base(it), { ext: '' }));
        const cats = (items) => {
            const seen = new Map();
            items.forEach((x) => { if (!seen.has(x.categoryId)) seen.set(x.categoryId, { id: x.categoryId, name: x.categoryName || 'Otros' }); });
            return Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name));
        };
        return {
            updatedAt: Date.now(),
            account: { user: account.user_info, server: account.server_info },
            live: [],
            vod,
            series,
            categories: { live: [], vod: cats(vod), series: cats(series) }
        };
    }

    _streams(it) {
        const list = ((it.MediaSources || [])[0] || {}).MediaStreams || it.MediaStreams || [];
        const v = list.find((s) => s.Type === 'Video');
        const a = list.find((s) => s.Type === 'Audio' && s.IsDefault) || list.find((s) => s.Type === 'Audio');
        return {
            video: v ? { codec_name: String(v.Codec || '').toLowerCase(), width: v.Width || 0, height: v.Height || 0 } : null,
            audio: a ? { codec_name: String(a.Codec || '').toLowerCase(), channels: a.Channels || 0 } : null,
            bitrate: Math.round((((it.MediaSources || [])[0] || {}).Bitrate || 0) / 1000)
        };
    }

    _people(it) {
        const people = it.People || [];
        const actors = people.filter((p) => p.Type === 'Actor' || p.Type === 'GuestStar');
        return {
            cast: actors.map((p) => p.Name).join(', '),
            castList: actors.slice(0, 15).map((p) => ({ name: p.Name, character: p.Role || '', photo: p.PrimaryImageTag ? this.image(p.Id, 'Primary', p.PrimaryImageTag, 185) : '' })),
            director: people.filter((p) => p.Type === 'Director').map((p) => p.Name).join(', '),
            composer: people.filter((p) => p.Type === 'Composer').map((p) => p.Name).join(', ')
        };
    }

    /** Ficha de una película o de una serie (con sus temporadas y episodios). */
    async info(kind, id, base) {
        const it = await this.get('/Users/' + this.session.userId + '/Items/' + id, { Fields: FIELDS + ',People,MediaStreams,ProductionLocations' }, { timeout: 20000 });
        const out = Object.assign({}, base, this._base(it), { id: base.id, categoryId: base.categoryId }, this._people(it), this._streams(it), {
            releaseDate: it.PremiereDate || '',
            country: (it.ProductionLocations || [])[0] || '',
            youtube: ((it.RemoteTrailers || [])[0] || {}).Url || ''
        });
        if (kind !== 'series') return out;
        // Los episodios se piden como elementos de la serie: es la consulta que entienden igual
        // Jellyfin y Emby (la de «/Shows/…/Episodes» cambia de un servidor a otro).
        const r = await this.get('/Users/' + this.session.userId + '/Items', {
            ParentId: id, IncludeItemTypes: 'Episode', Recursive: 'true', SortBy: 'ParentIndexNumber,IndexNumber',
            Fields: 'Overview,MediaSources,MediaStreams,RunTimeTicks,PremiereDate,CommunityRating,Container', EnableImageTypes: 'Primary', ImageTypeLimit: 1, EnableUserData: 'true'
        }, { timeout: 60000 });
        const seasons = new Map();
        ((r && r.Items) || []).forEach((ep) => {
            const number = Number(ep.ParentIndexNumber);
            // Los especiales (temporada 0) y los episodios sin número no tienen sitio en la lista.
            if (!(number > 0) || ep.IndexNumber == null) return;
            if (!seasons.has(number)) seasons.set(number, { number, name: ep.SeasonName || ('Temporada ' + number), poster: '', overview: '', episodes: [] });
            const src = (ep.MediaSources || [])[0] || {};
            seasons.get(number).episodes.push(Object.assign({
                id: String(ep.Id),
                seriesId: base.id,
                season: number,
                num: Number(ep.IndexNumber),
                title: ep.Name || ('Episodio ' + ep.IndexNumber),
                ext: String(src.Container || 'mp4').split(',')[0],
                plot: ep.Overview || '',
                durationSecs: ep.RunTimeTicks ? Math.round(ep.RunTimeTicks / TICKS) : 0,
                duration: '',
                thumb: this.image(ep.Id, 'Primary', (ep.ImageTags || {}).Primary, 400),
                rating: ep.CommunityRating || 0,
                airDate: ep.PremiereDate || '',
                server: this._serverState(ep)
            }, this._streams(ep)));
        });
        out.seasons = Array.from(seasons.values()).sort((a, b) => a.number - b.number);
        out.seasons.forEach((s) => s.episodes.sort((a, b) => a.num - b.num));
        return out;
    }

    /**
     * Marcas de intro y de créditos de un título, en segundos: [{ type: 'intro' | 'credits', start, end }]. Jellyfin 10.10+ las
     * publica como «segmentos de medios»; Jellyfin y Emby antiguos las ponen como marcas en los capítulos. Si no hay, [].
     */
    /** Colecciones (BoxSet) del servidor: [{ id, name, poster, ids: [id de cada título] }], solo las de 2 títulos o más. */
    async collections(limit = 30) {
        if (!this.session) await this.login();
        const uid = this.session.userId;
        const r = await this.get('/Users/' + uid + '/Items', { IncludeItemTypes: 'BoxSet', Recursive: 'true', SortBy: 'SortName', EnableImageTypes: 'Primary', ImageTypeLimit: 1, Limit: limit }, { timeout: 30000 });
        const out = [];
        for (const b of (r && r.Items) || []) {
            const kids = await this.get('/Users/' + uid + '/Items', { ParentId: b.Id, Limit: 200 }, { timeout: 30000 }).catch(() => null);
            const ids = ((kids && kids.Items) || []).map((x) => String(x.Id));
            if (ids.length >= 2) out.push({ id: String(b.Id), name: b.Name || '', poster: this.image(b.Id, 'Primary', (b.ImageTags || {}).Primary, 500), ids });
        }
        return out;
    }

    /** Petición con cuerpo JSON y respuesta JSON (o nada). 401/403 → «forbidden»: identificar títulos exige ser administrador del servidor. */
    async _json(method, path, body, params) {
        if (!this.session) await this.login();
        const send = () => this.fetch(this._url(path, params), { method, headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: body == null ? undefined : JSON.stringify(body) });
        let r = await send();
        if (r.status === 401) {
            await this.login();
            r = await send();
        }
        if (r.status === 401 || r.status === 403) throw fail('forbidden', r.status);
        if (!r.ok) throw fail('http', r.status);
        try { return await r.json(); } catch (e) { return null; }
    }

    /** Candidatos para «Corregir emparejado» (la identificación de Jellyfin/Emby): [{ key, title, year, thumb, overview, source, raw }]. */
    async matchSearch(kind, id, { title = '', year = '' } = {}) {
        const body = { SearchInfo: { Name: title }, ItemId: String(id) };
        if (year) body.SearchInfo.Year = Number(year) || undefined;
        const res = await this._json('POST', '/Items/RemoteSearch/' + (kind === 'series' ? 'Series' : 'Movie'), body);
        return (Array.isArray(res) ? res : []).slice(0, 12).map((x, i) => ({ key: String(i), title: x.Name || '', year: x.ProductionYear ? String(x.ProductionYear) : '', thumb: x.ImageUrl || '', overview: x.Overview || '', source: x.SearchProviderName || '', raw: x }));
    }

    async matchApply(kind, id, candidate) {
        await this._json('POST', '/Items/RemoteSearch/Apply/' + id, candidate.raw, { ReplaceAllImages: 'true' });
    }

    async segments(id, durationSecs = 0) {
        try {
            if (this.kind === 'jellyfin') {
                const r = await this.get('/MediaSegments/' + id, null, { timeout: 8000 });
                const list = ((r && r.Items) || []).map((s) => ({
                    type: /^intro$/i.test(s.Type) ? 'intro' : /^(outro|credits)$/i.test(s.Type) ? 'credits' : '',
                    start: Number(s.StartTicks) / TICKS, end: Number(s.EndTicks) / TICKS
                }));
                const clean = cleanSegments(list, durationSecs);
                if (clean.length) return clean;
            }
        } catch (e) { /* servidor sin segmentos: se prueba con los capítulos */ }
        try {
            const it = await this.get('/Users/' + this.session.userId + '/Items/' + id, { Fields: 'Chapters,RunTimeTicks' }, { timeout: 8000 });
            const dur = durationSecs || (it && it.RunTimeTicks ? it.RunTimeTicks / TICKS : 0);
            return segmentsFromChapters((it && it.Chapters) || [], dur, TICKS);
        } catch (e) {
            return [];
        }
    }

    /** POST con sesión (igual que `get`: si el token ya no vale, entra de nuevo una vez). Las respuestas vacías son normales. */
    async _post(path, body) {
        if (!this.session) await this.login();
        const send = () => this.fetch(this._url(path), { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: body == null ? undefined : JSON.stringify(body) });
        let r = await send();
        if (r.status === 401) {
            await this.login();
            r = await send();
        }
        if (!r.ok) throw fail('http', r.status);
    }

    /**
     * Cuenta al servidor lo que se está viendo (posición en segundos). Usa las sesiones de reproducción de Jellyfin y Emby:
     * una al empezar, avisos de progreso y otra al parar; con eso el servidor guarda «continuar viendo» y lo marca visto.
     * `finished`: además se marca como visto de forma explícita.
     */
    async report(id, { position = 0, stopped = false, finished = false } = {}) {
        if (!this.session) await this.login();
        const body = { ItemId: String(id), PositionTicks: Math.max(0, Math.round(position * TICKS)), PlayMethod: 'DirectPlay', CanSeek: true, IsPaused: false, IsMuted: false };
        if (stopped) {
            this._playing.delete(id);
            await this._post('/Sessions/Playing/Stopped', body);
        } else if (this._playing.has(id)) {
            await this._post('/Sessions/Playing/Progress', body);
        } else {
            this._playing.add(id);
            await this._post('/Sessions/Playing', body);
        }
        if (finished) await this._post('/Users/' + this.session.userId + '/PlayedItems/' + id, null);
    }

    /** El fichero original, sin recodificar, con saltos por rangos. No se indica la fuente: cada
        servidor la nombra a su manera (en Emby no coincide con el id del título) y sin ella
        ambos sirven la principal. */
    _stream(id, ext) {
        return this._url('/Videos/' + id + '/stream' + (ext ? '.' + ext : ''), { static: 'true' });
    }

    vodUrl(id, ext) {
        return this._stream(id, ext);
    }

    seriesUrl(id, ext) {
        return this._stream(id, ext);
    }

    liveUrl() {
        return '';
    }

    xmltvUrl() {
        return '';
    }
}
